<script setup>
import { ref, onMounted, onBeforeUnmount, computed } from 'vue';
import { usePedidoStore } from '@/stores/pedido';
import usePedidoActions from '@/composables/usePedidoActions';
import usePedidoHelpers from '@/composables/usePedidoHelpers';
import useModalManagement from '@/composables/useModalManagement';
import useConfirmation from '@/composables/useConfirmation';
import useCronometro from '@/composables/useCronometro';
import ConfirmationModal from '@/components/modals/ConfirmationModal.vue';
import RejetiarPedidoModal from '@/components/modals/RejeitarPedidoModal.vue';
import PedidoDeliveryTimeModal from '@/components/modals/PedidoDeliveryTimeModal.vue';
import DesativarRevendedorModal from '@/components/modals/DesativarRevendedorModal.vue';
import useFormatters from '@/composables/useFormatters';
import { useRevendaStore } from '@/stores/revenda';

const props = defineProps({
  pedido: {
    type: Object,
    required: true
  }
});

// Usar o composable para formatação
const { formatMoeda, formatDate, formatCpf } = useFormatters();

// Inicializar o timer de pedido se o pedido tem um cronômetro a ser exibido
const showCountdown = props.pedido.exibir_cronometro === true;
const { countdownDisplay, percentRemaining, countdownColor, isExpired } = showCountdown
  ? useCronometro(props.pedido.dtpedido, 600)
  : { countdownDisplay: ref(''), percentRemaining: ref(0), countdownColor: ref(''), isExpired: ref(false) };

const pedidoStore = usePedidoStore();
const {
  copyToClipboard,
  openWhatsApp,
  handleAvisarPedido,
  handleNotificarEstouNaPorta,
  handleClienteBlock,
  handleTogglePendente,
  handleAplicarSolucao,
  handleAlterarStatus,
  handleNotificarAtraso,
  ativarRevenda,
  loadNotificarEstouNaPorta,
  loadingClienteBlock,
  loadingRevendedorAction,
  isPedidoOperationLoading,
  showNotification,
  showErrorNotification,
  rejectionModalOpen,
  rejectionLoading,
  handleRejectPedido,
  handleRemakePedido,
  pedidoToReject,
  deliveryTimeModalOpen,
  deliveryTimeLoading,
  pedidoToDelivery,
  handleSairParaEntrega,
  removerMereceAtencaoCliente,
  loadingRemoveMereceAtencao,
} = usePedidoActions();

const { cardDisplayInfo, isNullOrEmpty, getSuspeitoDescricao } = usePedidoHelpers();

// Get the modal management functions
const modalManager = useModalManagement();
const {
  openEditOrderModal,
  openObservationModal,
  openAssistanceModal,
  deactivateModalOpen,
  selectedRevenda
} = modalManager;

// Add a method to open the deactivate modal
const openDesativarRevendaModal = () => {
  if (props.pedido?.revenda) {
    selectedRevenda.value = props.pedido.revenda;
    deactivateModalOpen.value = true;
  }
};

const {
  confirmationOpen,
  confirmationConfig,
  confirm,
  handleConfirm: originalHandleConfirm,
  handleCancel: originalHandleCancel,
  setLoading
} = useConfirmation();

// State for dropdowns
const selectedStatus = ref(null);
const selectedSolution = ref(null);

// Track when an operation is being performed
const isChangingStatus = (statusId) => {
  return isPedidoOperationLoading(props.pedido.id, `status-${statusId}`);
};

const isApplyingSolution = (solutionId) => {
  return isPedidoOperationLoading(props.pedido.id, `solucao-${solutionId}`);
};

// Handle status selection
const onStatusChange = async (statusId) => {
  if (!statusId) return;

  // rejeitado (status 4) e "saiu para entrega" (status 8)
  if (statusId === '4' || statusId === '8') {
    handleAlterarStatus(props.pedido, statusId);
    selectedStatus.value = null;
    return;
  }

  const statusLabels = {
    '2': 'aceitar',
    '4': 'rejeitar',
    '8': 'marcar como saiu para entrega',
    '3': 'marcar como entregue',
    '5': 'cancelar'
  };

  try {
    if (window.pedidoModalOpenCount === undefined) window.pedidoModalOpenCount = 0;
    window.pedidoModalOpenCount++;

    const action = statusLabels[statusId] || 'alterar o status de';

    await confirm({
      title: 'Alterar Status',
      message: `Tem certeza que deseja ${action} este pedido?`,
      confirmColor: 'primary',
      confirmText: 'Sim, Alterar',
      urgent: true
    });

    setLoading(true);

    const success = await handleAlterarStatus(props.pedido, statusId);

    if (!success) {
      throw new Error('Failed to update status');
    }
  } catch (error) {
    if (error !== 'User cancelled') {
    }
  } finally {

    if (window.pedidoModalOpenCount) window.pedidoModalOpenCount--;
    setLoading(false);
    selectedStatus.value = null;
  }
};

const onSolutionChange = async (solutionId) => {
  if (!solutionId) return;

  const solutionLabels = {
    '1': 'avisar pedido',
    '2': 'ligar para cliente',
    '3': 'salvar pedido',
    '4': 'verificar pedidos atrasados'
  };

  const action = solutionLabels[solutionId] || 'aplicar solução';

  try {
    if (window.pedidoModalOpenCount === undefined) window.pedidoModalOpenCount = 0;
    window.pedidoModalOpenCount++;

    await confirm({
      title: 'Confirmar Ação',
      message: `Tem certeza que deseja ${action} para este pedido?`,
      confirmColor: 'primary',
      confirmText: 'Sim, Confirmar',
      urgent: true
    });

    setLoading(true);
    const success = await handleAplicarSolucao(props.pedido, solutionId);

    if (!success) {
      throw new Error('Failed to apply solution');
    }
  } catch (error) {
  } finally {
    if (window.pedidoModalOpenCount) window.pedidoModalOpenCount--;
    setLoading(false);
    selectedSolution.value = null;
  }
};

const removingSuspeito = ref(false);

const handleSuspeitoToggle = async (val) => {
  if (val) {
    pedido.suspeito = 1;
  } else {
    try {
      removingSuspeito.value = true;
      const result = await pedidoStore.removerSuspeitoPedido(props.pedido.id);

      if (result.success && result.data) {
        props.pedido.suspeito = 0;
        showNotification('Pedido removido de suspeitos!');
      } else {
        props.pedido.suspeito = 1;
        showErrorNotification(result.message);
      }
    } catch (error) {
      // Restore previous state
      props.pedido.suspeito = 1;
      showErrorNotification('Falha ao atualizar status suspeito do pedido');
    } finally {
      removingSuspeito.value = false;
    }
  }
};

onMounted(() => {
  if (!window.isAnyPedidoModalOpen) {
    window.isAnyPedidoModalOpen = () => {
      return rejectionModalOpen.value ||
        deliveryTimeModalOpen.value ||
        confirmationOpen.value ||
        (window.pedidoModalOpenCount && window.pedidoModalOpenCount > 0);
    };
  }
});

const avisandoPedido = ref(false);

const handleAvisarPedidoLocal = async (pedido) => {
  if (avisandoPedido.value) return;

  try {
    avisandoPedido.value = true;
    await handleAvisarPedido(pedido);
  } finally {
    avisandoPedido.value = false;
  }
};

const notificandoAtraso = ref(false);
const pedidoParaAtrasoConfirmation = ref(null);
const notificandoAtrasoCliente = ref(false);
const pedidoParaAtrasoClienteConfirmation = ref(null);

// --- Novas refs para Ligação de Atraso ---
const ligando = ref(false);
const pedidoParaLigacaoConfirmation = ref(null);

// Loading para toggle de ligações da revenda
const ligacoesLoading = ref(false);
const revendaStore = useRevendaStore();

/**
 * Toggle habilitação de ligações da revenda.
 * Usa os endpoints: ATIVAR_LIGACAO_REVENDA (post) e DESATIVAR_LIGACAO_REVENDA (del)
 */
const handleToggleLigacaoRevenda = async (newVal) => {
  const revendaId = props.pedido?.revenda?.id;
  if (!revendaId) {
    showErrorNotification('Revenda inválida para alterar ligações');
    return;
  }

  // comportamento otimista: aplica UI imediatamente e reverte em caso de erro
  const previous = !!props.pedido.revenda?.ligacao_habilitada;
  props.pedido.revenda.ligacao_habilitada = newVal;
  ligacoesLoading.value = true;

  try {
    const res = await revendaStore.alterarStatusLigacaoRevenda(revendaId, newVal);
    if (!res || !res.success) {
      // reverter e mostrar erro
      props.pedido.revenda.ligacao_habilitada = previous;
      showErrorNotification(res?.message || 'Falha ao atualizar recebimento de ligações');
      return;
    }

    showNotification(res.message || 'Configuração de ligações atualizada');
  } catch (err) {
    props.pedido.revenda.ligacao_habilitada = previous;
    console.error('Erro ao alternar ligações da revenda:', err);
    showErrorNotification(err?.message || 'Erro ao atualizar opção de ligações');
  } finally {
    ligacoesLoading.value = false;
  }
};

const handleNotificarAtrasoLocal = async (pedido) => {
  if (notificandoAtraso.value) return;

  try {
    pedidoParaAtrasoConfirmation.value = pedido;

    confirmationConfig.title = 'Confirmar envio';
    confirmationConfig.message = 'Tem certeza que deseja enviar uma notificação de atraso para a revenda?';
    confirmationConfig.confirmText = 'Sim, Enviar';
    confirmationConfig.cancelText = 'Cancelar';
    confirmationConfig.confirmColor = 'primary';
    confirmationConfig.urgent = true;

    if (window.pedidoModalOpenCount === undefined) window.pedidoModalOpenCount = 0;
    window.pedidoModalOpenCount++;

    confirmationOpen.value = true;
  } catch (error) {
    if (window.pedidoModalOpenCount) window.pedidoModalOpenCount--;
  }
};

const handleAtrasoConfirmation = async () => {
  try {
    if (!pedidoParaAtrasoConfirmation.value) {
      return;
    }

    notificandoAtraso.value = true;
    setLoading(true);

    const result = await handleNotificarAtraso(pedidoParaAtrasoConfirmation.value);

    confirmationOpen.value = false;
    if (result) {
      // Marcar que a notificação foi enviada com sucesso para esconder o botão
      if (pedidoParaAtrasoConfirmation.value && pedidoParaAtrasoConfirmation.value.id) {
        // Adicionar o log fake para que hasAtrasoRevendedorNotification retorne true
        if (!pedidoParaAtrasoConfirmation.value.log_tmkt) {
          pedidoParaAtrasoConfirmation.value.log_tmkt = [];
        }

        pedidoParaAtrasoConfirmation.value.log_tmkt.push({
          statuspedido: 11,
        });
      }

      showNotification('Notificação de atraso enviada ao revendedor com sucesso!');
    } else {
      showErrorNotification(result.message || 'Erro ao enviar notificação de atraso ao revendedor');
    }
  } catch (error) {
    confirmationOpen.value = false;
  } finally {
    notificandoAtraso.value = false;
    setLoading(false);
    pedidoParaAtrasoConfirmation.value = null;
  }
};

const handleNotificarAtrasoClienteLocal = async (pedido) => {
  if (notificandoAtrasoCliente.value) return;

  try {
    pedidoParaAtrasoClienteConfirmation.value = pedido;

    confirmationConfig.title = 'Confirmar envio';
    confirmationConfig.message = 'Tem certeza que deseja enviar uma notificação de atraso para o cliente?';
    confirmationConfig.confirmText = 'Sim, Enviar';
    confirmationConfig.cancelText = 'Cancelar';
    confirmationConfig.confirmColor = 'primary';
    confirmationConfig.urgent = true;

    if (window.pedidoModalOpenCount === undefined) window.pedidoModalOpenCount = 0;
    window.pedidoModalOpenCount++;

    confirmationOpen.value = true;
  } catch (error) {
    if (window.pedidoModalOpenCount) window.pedidoModalOpenCount--;
  }
};

const handleAtrasoClienteConfirmation = async () => {
  try {
    if (!pedidoParaAtrasoClienteConfirmation.value) {
      return;
    }

    notificandoAtrasoCliente.value = true;
    setLoading(true);

    const result = await pedidoStore.notificarAtrasoCliente(pedidoParaAtrasoClienteConfirmation.value.id);

    confirmationOpen.value = false;

    if (result) {
      if (pedidoParaAtrasoClienteConfirmation.value && pedidoParaAtrasoClienteConfirmation.value.id) {
        if (!pedidoParaAtrasoClienteConfirmation.value.log_tmkt) {
          pedidoParaAtrasoClienteConfirmation.value.log_tmkt = [];
        }

        pedidoParaAtrasoClienteConfirmation.value.log_tmkt.push({
          statuspedido: 10,
        });
      }

      showNotification('Notificação de atraso enviada ao cliente com sucesso!');
    } else {
      showErrorNotification(result.message || 'Erro ao enviar notificação de atraso ao cliente');
    }
  } catch (error) {
    confirmationOpen.value = false;
  } finally {
    notificandoAtrasoCliente.value = false;
    setLoading(false);
    pedidoParaAtrasoClienteConfirmation.value = null;
  }
};

const clienteParaMereceAtencaoRemoval = ref(null);

const handleRemoveMereceAtencao = async () => {
  if (!props.pedido?.cliente?.id) return;

  try {
    clienteParaMereceAtencaoRemoval.value = props.pedido.cliente;

    confirmationConfig.title = 'Confirmar remoção';
    confirmationConfig.message = 'Tem certeza que deseja remover a marcação de "Merece Atenção" deste cliente?';
    confirmationConfig.confirmText = 'Sim, Remover';
    confirmationConfig.cancelText = 'Cancelar';
    confirmationConfig.confirmColor = 'primary';
    confirmationConfig.urgent = true;

    if (window.pedidoModalOpenCount === undefined) window.pedidoModalOpenCount = 0;
    window.pedidoModalOpenCount++;

    confirmationOpen.value = true;
  } catch (error) {
    if (window.pedidoModalOpenCount) window.pedidoModalOpenCount--;
  }
};

const handleMereceAtencaoRemovalConfirmation = async () => {
  try {
    if (!clienteParaMereceAtencaoRemoval.value) {
      return;
    }

    loadingRemoveMereceAtencao.value = true;
    setLoading(true);

    const result = await removerMereceAtencaoCliente(clienteParaMereceAtencaoRemoval.value.id);

    confirmationOpen.value = false;

    if (result.success && clienteParaMereceAtencaoRemoval.value) {
      clienteParaMereceAtencaoRemoval.value.merece_atencao = false;
      clienteParaMereceAtencaoRemoval.value.revenda_merece_atencao = null;

      showNotification(result.message || 'Cliente não precisa mais de atenção especial');
    }
  } catch (error) {
    confirmationOpen.value = false;
    showErrorNotification('Erro ao remover atenção especial do cliente');
  } finally {
    loadingRemoveMereceAtencao.value = false;
    setLoading(false);
    clienteParaMereceAtencaoRemoval.value = null;
  }
};

// Novo handler para abrir confirmação de ligar agora
const handleLigacaoAtrasoLocal = async (pedido) => {
  if (ligando.value) return;

  try {
    pedidoParaLigacaoConfirmation.value = pedido;

    confirmationConfig.title = 'Solicitar Ligação';
    confirmationConfig.message = `Deseja solicitar uma ligação para o revendedor responsável pelo pedido #${pedido.id} que está em atraso?`;
    confirmationConfig.confirmText = 'Sim, Solicitar';
    confirmationConfig.cancelText = 'Cancelar';
    confirmationConfig.confirmColor = 'primary';
    confirmationConfig.urgent = true;

    if (window.pedidoModalOpenCount === undefined) window.pedidoModalOpenCount = 0;
    window.pedidoModalOpenCount++;

    confirmationOpen.value = true;
  } catch (error) {
    if (window.pedidoModalOpenCount) window.pedidoModalOpenCount--;
  }
};

// Função que executa a chamada ao endpoint após confirmação
const handleLigacaoConfirmation = async () => {
  if (!pedidoParaLigacaoConfirmation.value) return;

  ligando.value = true;
  setLoading(true);
  try {
    const result = await pedidoStore.solicitarLigacaoAtraso(pedidoParaLigacaoConfirmation.value.id);

    confirmationOpen.value = false;

    if (result && result.success) {
      showNotification(result.message || 'Ligação agendada com sucesso!');
    } else {
      // Exibir mensagem de erro retornada pela API como alert
      const msg = result?.message || 'Falha ao agendar ligação';
      showErrorNotification(msg);
    }
  } catch (error) {
    // Tentar extrair mensagem do payload de erro (ex.: { status: "error", message: "..." })
    const apiMessage = error?.response?.data?.message || error?.message || 'Erro ao agendar ligação';
    showErrorNotification(apiMessage);
  } finally {
    ligando.value = false;
    setLoading(false);
    pedidoParaLigacaoConfirmation.value = null;
  }
};


const handleConfirm = () => {
  try {
    if (pedidoParaAtrasoConfirmation.value) {
      handleAtrasoConfirmation();
    } else if (pedidoParaAtrasoClienteConfirmation.value) {
      handleAtrasoClienteConfirmation();
    } else if (pedidoParaLigacaoConfirmation.value) {
      handleLigacaoConfirmation();
    } else if (clienteParaMereceAtencaoRemoval.value) {
      handleMereceAtencaoRemovalConfirmation();
    } else {
      originalHandleConfirm();
    }
  } finally {
    if (window.pedidoModalOpenCount) window.pedidoModalOpenCount--;
  }
};

// Atualizar handleCancel para limpar o novo estado quando cancelado
const handleCancel = () => {
  try {
    if (pedidoParaAtrasoConfirmation.value) {
      pedidoParaAtrasoConfirmation.value = null;
    } else if (pedidoParaAtrasoClienteConfirmation.value) {
      pedidoParaAtrasoClienteConfirmation.value = null;
    } else if (pedidoParaLigacaoConfirmation.value) {
      pedidoParaLigacaoConfirmation.value = null;
    } else if (clienteParaMereceAtencaoRemoval.value) {
      clienteParaMereceAtencaoRemoval.value = null;
    }
    originalHandleCancel();
  } finally {
    if (window.pedidoModalOpenCount) window.pedidoModalOpenCount--;
  }
};

onBeforeUnmount(() => {
  if (confirmationOpen.value || rejectionModalOpen.value || deliveryTimeModalOpen.value) {
    if (confirmationOpen.value && window.pedidoModalOpenCount)
      window.pedidoModalOpenCount--;

    if (rejectionModalOpen.value && window.pedidoModalOpenCount)
      window.pedidoModalOpenCount--;

    if (deliveryTimeModalOpen.value && window.pedidoModalOpenCount)
      window.pedidoModalOpenCount--;

    confirmationOpen.value = false;
    rejectionModalOpen.value = false;
    deliveryTimeModalOpen.value = false;
  }
});

// Filtrar por razao social
const filtrarRazaoSocial = () => {
  if (props.pedido.revenda?.revendedor?.razaosocial) {
    pedidoStore.filtros.razaoSocial = props.pedido.revenda.revendedor.razaosocial;
    pedidoStore.aplicarFiltros();
  }
};

const hasAtrasoRevendedorNotification = (pedido) => {
  if (!pedido.log_tmkt || !Array.isArray(pedido.log_tmkt)) return false;

  return pedido.log_tmkt.some(log =>
    log.statuspedido == 11
  );
};

const hasAtrasoClienteNotification = (pedido) => {
  if (!pedido.log_tmkt || !Array.isArray(pedido.log_tmkt)) return false;

  return pedido.log_tmkt.some(log =>
    log.statuspedido == 10
  );
};

const verificarAceitoPeloTMKT = (pedido) => {
  if (!pedido.log_tmkt || !Array.isArray(pedido.log_tmkt)) return false;

  return pedido.log_tmkt.some(log => log.statuspedido == 2);
};

const handleRevendedorDeactivated = (updatedRevendedor) => {
  if (selectedRevenda.value?.revendedor) {
    selectedRevenda.value.revendedor.ativo = updatedRevendedor.ativo;
    if (props.pedido.revenda?.revendedor?.id === updatedRevendedor.id) {
      props.pedido.revenda.revendedor.ativo = updatedRevendedor.ativo;
    }
  }
};

// Add this method to toggle attention status
const toggleAtencao = (value) => {
  if (value) {
    pedidoStore.addPedidoEmAtencao(props.pedido);
  } else {
    pedidoStore.removePedidoEmAtencao(props.pedido.id);
  }
};

// Propriedade computada para desabilitar o botão "Ligar Agora"
const desabilitarLigarAgora = computed(() => {
  return ligando.value || !props.pedido.revenda?.ligacao_habilitada || !props.pedido.atrasado;
});

const textoTooltipLigarAgora = computed(() => {
  if (ligando.value) {
    return 'Aguardando...';
  }
  if (!props.pedido.revenda?.ligacao_habilitada) {
    return 'Revenda não aceita ligações';
  }
  if (!props.pedido.atrasado) {
    return 'Somente para pedidos em atraso';
  }
  return 'Solicitar ligação de atraso ao revendedor';
});
</script>

<template>
  <v-card class="mb-3 order-card" :class="{ 'in-attention': pedidoStore.isPedidoEmAtencao(pedido.id) }"
    variant="outlined" :style="cardDisplayInfo(pedido).backgroundColor.value">
    <!-- Card header with tabs -->
    <v-card-title class="text-subtitle-1 pb-1">
      <v-tabs v-model="pedidoStore.activeTab[pedido.id]" align-tabs="start" color="primary" density="compact"
        class="compact-tabs">
        <v-tab class="compact-tab" value="tabPedido">Pedido</v-tab>
        <v-tab class="compact-tab" value="tabCliente">Cliente</v-tab>
        <v-tab class="compact-tab position-relative" value="tabRevenda">
          Revenda
          <v-badge v-if="pedido.total_pendentes_revendedor > 1" :content="pedido.total_pendentes_revendedor"
            color="error" :model-value="true" location="top end" offset-x="-8" offset-y="-8"
            class="pending-orders-badge" title="Pedidos pendentes deste revendedor - Clique para filtrar"
            @click.stop="filtrarRazaoSocial" style="cursor: pointer;"></v-badge>
        </v-tab>
        <v-tab class="compact-tab" value="tabAcoes">Ações</v-tab>
      </v-tabs>
    </v-card-title>
    <v-divider></v-divider>

    <!-- Timer para pedidos a aceitar -->
    <div v-if="pedido.exibir_cronometro === true" class="countdown-container">
      <div class="countdown-label" :class="[`text-${countdownColor}`]">
        Tempo para aceitar: <span class="countdown-time">{{ countdownDisplay }}</span>
      </div>
      <v-progress-linear :model-value="percentRemaining" :color="countdownColor" height="4" rounded></v-progress-linear>
    </div>

    <!-- Card content -->
    <v-card-text class="pt-1">
      <!-- Pedido tab -->
      <div v-if="pedidoStore.activeTab[pedido.id] === 'tabPedido'">
        <div class="d-flex justify-space-between">
          <span>
            <strong>#{{ pedido.id }}</strong>
            <v-icon class="copy-icon" size="x-small" color="primary"
              @click="copyToClipboard(pedido.id, 'Número do pedido')">mdi-content-copy</v-icon>
          </span>
          <span
            :style="{ color: pedido.agendado === true ? 'red' : 'inherit', fontWeight: pedido.agendado === true ? 'bold' : 'normal' }">{{
              formatDate(pedido.dtprevisao) }}</span>
        </div>

        <!-- Adicionar uma descrição personalizada para um pedido suspeito -->
        <v-chip v-if="pedido.suspeito > 2" color="error" size="small" prepend-icon="mdi-alert-circle" variant="flat"
          class="d-flex justify-center text-white" style="width: 100%; font-size: 14px;">
          {{ getSuspeitoDescricao(pedido.suspeito) }}
        </v-chip>

        <div v-if="pedido.pedsucessor" class="pedido-refeito mb-1">
          <v-icon size="small" color="primary" class="mr-1">mdi-alert-circle</v-icon>
          <span color="primary">Pedido foi refeito no #{{ pedido.pedsucessor }}</span>
          <v-icon class="copy-icon" size="x-small" color="primary"
            @click="copyToClipboard(pedido.pedsucessor, 'Número do novo pedido')">mdi-content-copy</v-icon>
        </div>

        <!-- Icone e descrição de status atual do pedido -->
        <div class="mb-1">
          <v-icon size="small" :color="cardDisplayInfo(pedido).statusDescription.value.iconColor" class="mr-1">{{
            cardDisplayInfo(pedido).statusDescription.value.icon }}</v-icon>
          <span>{{ cardDisplayInfo(pedido).statusDescription.value.text }}</span>
        </div>

        <!-- Add expired warning for status=1 pedidos where countdown expired -->
        <div v-if="pedido.status === '1' && isExpired" class="expired-warning mb-1">
          <v-icon size="small" color="error" class="mr-1">mdi-alert-circle</v-icon>
          <span>Tempo para aceitar expirado!</span>
        </div>
        <div class="mb-1"
          title="Total de pedidos concluídos nos últimos 30 dias, considerando todas as revendas do revendedor.">
          <strong style="cursor: help">Pedidos (30 dias): </strong>
          <span>
            {{ typeof pedido.total_pedidos_30_dias_revendedor === 'number'
              ? pedido.total_pedidos_30_dias_revendedor
              : 'Dados temporariamente indisponíveis'
            }}
          </span>
        </div>
        <div class="mb-1"><strong>Data Pedido:</strong> {{ formatDate(pedido.dtpedido) }}</div>
        <div class="mb-1">
          <strong>Origem:</strong> {{ pedido.origem }}
        </div>
        <div v-if="pedido.pedant">
          <strong>Pedido anterior:</strong> #{{ pedido.pedant }}
          <v-icon class="copy-icon" size="x-small" color="primary"
            @click="copyToClipboard(pedido.pedant, 'Número do pedido')">mdi-content-copy</v-icon>
        </div>
        <div class="mb-1">
          <strong>Produto:</strong> {{ pedido.quantidade }}x {{ pedido.produto.tipoproduto.tipo }} - {{
            pedido.produto.fabricante.nome }}
        </div>

        <!-- Badge Gás do Povo -->
        <div v-if="pedido.gas_do_povo === true || pedido.gas_do_povo === 1" class="mb-2">
          <v-chip color="green-darken-1" size="small" prepend-icon="mdi-fire" variant="flat"
            class="d-flex justify-center text-white font-weight-medium" 
            style="width: 100%;">
            Gás do Povo
          </v-chip>
        </div>
        <div class="mb-1">
          <strong>Valor Produto:</strong> {{ formatMoeda(pedido.vlproduto) }}
        </div>
        <div class="mb-1">
          <strong>Valor Pedido:</strong> {{ formatMoeda(pedido.vlpedido) }}
        </div>

        <template v-if="pedido?.produto?.tipoproduto?.categoria === 'AGUA'">
          <div class="mb-1">
            <strong>Valor taxa de serviço:</strong> {{ formatMoeda(pedido?.taxa_servico) }}
          </div>
          <div class="mb-1">
            <strong>Valor total:</strong> {{ formatMoeda(pedido?.valor_com_taxa) }}
          </div>
        </template>

        <div class="mb-1">
          <strong>Pag:</strong> {{ pedido.formapagamento }}
          <span v-if="pedido.idformapagamento === 2 && pedido.qtd_parcela > 1">
            ({{ pedido.qtd_parcela }}x de {{ formatMoeda(pedido.vl_parcela) }})
          </span>
          <span v-else-if="pedido.idformapagamento === 2 && pedido.qtd_parcela < 1">
            de crédito
          </span>
          <span v-if="pedido.idformapagamento === 1 && pedido.troco">
            (Troco {{ formatMoeda(pedido.troco) }})
          </span>
        </div>

        <div v-if="pedido.tempo_entrega != ''" class="mb-0">
          <strong>Tempo de Entrega:</strong> {{ pedido.tempo_entrega + " min" }}
        </div>

        <div v-if="pedido.observacao" class="mt-2 order-observation">
          <div class="d-flex align-center justify-space-between">
            <div><strong>Obs({{ pedido.atendente_id_obs }}):</strong>{{ pedido.observacao }}</div>
            <v-icon class="edit-icon" size="small" color="grey-darken-1"
              @click="openObservationModal(pedido, 'pedido')">mdi-pencil</v-icon>
          </div>
        </div>

        <div v-if="pedido.observacao_aplicativo" class="mt-2 order-observation">
          <div class="d-flex align-center justify-space-between">
            <div><strong>Obs p/ revenda: </strong>{{ pedido.observacao_aplicativo }}</div>
            <v-icon class="edit-icon" size="small" color="grey-darken-1"
              @click="openObservationModal(pedido, 'aplicativo')">mdi-pencil</v-icon>
          </div>
        </div>

        <!-- Status tags - Modified section to keep original layout -->
        <div class="mt-1 d-flex justify-space-between flex-wrap">
          <div v-if="pedido.cliente?.merece_atencao === true" class="merece-atencao-container"
            style="width: 49%; position: relative;">
            <v-chip color="amber-darken-3" size="small" prepend-icon="mdi-alert" variant="flat"
              title="Cliente merece atenção" class="d-flex justify-start text-black" style="width: 100%">
              {{ pedido.cliente?.revenda_merece_atencao === pedido.revenda?.id ?
                'Merece Atenção' :
                'Outra Revenda' }}
            </v-chip>

            <v-btn size="small" icon color="white" variant="text" class="remove-attention-btn"
              :disabled="loadingRemoveMereceAtencao" :loading="loadingRemoveMereceAtencao"
              @click="handleRemoveMereceAtencao" title="Remover marcação de Merece Atenção">
              <v-icon size="small" color="white">mdi-close</v-icon>
            </v-btn>
          </div>

          <v-chip v-if="pedido.agendado === true" size="small" prepend-icon="mdi-calendar-clock" variant="flat"
            class="d-flex justify-center text-black" style="background-color: #dca1f7; width: 49%">
            Agendado
          </v-chip>

          <v-chip v-if="pedido.atrasado" color="#FF2222" prepend-icon="mdi-clock-alert" variant="flat"
            density="comfortable" size="default" class="d-flex justify-center mt-1"
            style="width: 100%; font-weight: bold;">
            {{ pedido.tempo_atraso }} de atraso
          </v-chip>
        </div>

        <v-divider v-if="pedido.status === '1'" class="my-2"></v-divider>

        <v-btn v-if="pedido.status === '1'" block size="small" prepend-icon="mdi-bell-outline"
          style="background-color: #d43ab1;font-weight: 600;" @click="handleAvisarPedidoLocal(pedido)"
          :loading="avisandoPedido" :disabled="avisandoPedido">
          Avisar pedido
        </v-btn>

        <v-btn v-if="pedido.status === '2' && !hasAtrasoRevendedorNotification(pedido)" block size="small"
          prepend-icon="mdi-whatsapp" style="background-color: #25D366; color: white;font-weight: 600;"
          @click="handleNotificarAtrasoLocal(pedido)" :loading="notificandoAtraso" :disabled="notificandoAtraso"
          class="mt-1">
          ZAP ATRASO REVENDA
        </v-btn>

        <v-btn v-if="pedido.cliente.telefone1 && !hasAtrasoClienteNotification(pedido)" block size="small"
          prepend-icon="mdi-whatsapp" color="amber-darken-1" style="font-weight: 600;"
          @click="handleNotificarAtrasoClienteLocal(pedido)" :loading="notificandoAtrasoCliente"
          :disabled="notificandoAtrasoCliente" class="mt-2">
          ZAP CLIENTE
        </v-btn>

        <div v-if="pedido.suspeito > 0" class="px-2" style="margin-bottom: -10px;">
          <v-switch :model-value="!!pedido.suspeito" @update:model-value="handleSuspeitoToggle"
            :disabled="pedidoStore.loading || removingSuspeito" :loading="removingSuspeito" color="#ff0000" hide-details
            label="Pedido Suspeito" density="compact" class="suspicious-switch">
            <template v-slot:label>
              <span style="color: #000000; font-weight: bold;">Pedido Suspeito</span>
            </template>
          </v-switch>
        </div>
      </div>

      <!-- Cliente tab -->
      <div v-else-if="pedidoStore.activeTab[pedido.id] === 'tabCliente'">
        <div v-if="pedido.cliente.nome === ''" class="text-center py-4">
          <v-icon color="warning" size="large" class="mb-2">mdi-account-off</v-icon>
          <div class="text-h6">Cliente Excluído</div>
        </div>

        <div v-else>

          <div class="d-flex justify-space-between mb-1">
            <span>
              <strong>Nome:</strong> {{ pedido.cliente.nome }}
            </span>
            <v-icon title="Copiar dados do cliente" class="copy-icon mt-1" size="small" color="primary"
              @click="copyToClipboard('Nome: ' + pedido.cliente.nome + '\nTelefone: ' + pedido.cliente.telefone1 + '\nEndereço: ' + pedido.endereco_entrega, 'Dados do cliente')">mdi-content-copy</v-icon>
          </div>

          <div class="mb-1">
            <strong>Telefone:</strong> {{ pedido.cliente.telefone1 }}
            <v-icon title="Copiar telefone do cliente" class="copy-icon" size="x-small" color="primary"
              @click="copyToClipboard(pedido.cliente.telefone1, 'Telefone do cliente')">mdi-content-copy</v-icon>
            <v-icon class="action-icon whatsapp-icon" size="small" color="green" title="Notificar via WhatsApp"
              @click="openWhatsApp(pedido.cliente.telefone1, pedido.cliente.nome, pedido.id, 'cliente')">mdi-whatsapp</v-icon>
          </div>

          <!-- CPF do Cliente (se houver) -->
          <div v-if="pedido.cpf && pedido.cpf.trim() !== ''" class="mb-1">
            <strong>CPF:</strong> {{ formatCpf(pedido.cpf) }}
            <v-icon title="Copiar CPF do cliente" class="copy-icon" size="x-small" color="primary"
              @click="copyToClipboard(pedido.cpf, 'CPF do cliente')">mdi-content-copy</v-icon>
          </div>
        </div>

        <div class="mb-1">
          <strong>Endereço:</strong> {{ pedido.endereco_entrega }}
          <v-icon title="Copiar endereço do pedido" class="copy-icon" size="x-small" color="primary"
            @click="copyToClipboard(pedido.endereco_entrega, 'Endereço de entrega do pedido')">mdi-content-copy</v-icon>
        </div>

        <div class="mb-1"><strong>Data Cadastro:</strong> {{ pedido.cliente.dtcadastro }}</div>
        <div class="mb-1">
          <strong>Distância: </strong>
          <span :class="{ 'text-error': pedido.distancia_calculada > 1000 }">
            {{ pedido.distancia_calculada ? pedido.distancia_calculada.toFixed(2) + " km" : "Vazio" }}
          </span>
        </div>
        <div class="mb-1" v-if="pedido.cliente.ip">
          <strong>IP:</strong> {{ pedido.cliente.ip }}
          <v-icon class="copy-icon" size="x-small" color="primary"
            @click="copyToClipboard(pedido.cliente.ip, 'IP do cliente')">mdi-content-copy</v-icon>
        </div>
        <div v-if="pedido.cliente.nome !== ''">
          <v-divider class="my-2"></v-divider>
          <v-btn color="indigo" block class="mb-2" size="small" prepend-icon="mdi-map-marker"
            @click="handleNotificarEstouNaPorta(pedido)" :loading="loadNotificarEstouNaPorta"
            :disabled="loadNotificarEstouNaPorta">
            Notificar "Estou na porta"
          </v-btn>
          <v-btn :color="pedido.cliente.bloqueado ? 'success' : 'error'" block size="small"
            :prepend-icon="pedido.cliente.bloqueado ? 'mdi-account-check' : 'mdi-block-helper'"
            @click="handleClienteBlock(pedido)" :loading="loadingClienteBlock" :disabled="loadingClienteBlock">
            {{ pedido.cliente.bloqueado ? 'Desbloquear cliente' : 'Bloquear cliente' }}
          </v-btn>
        </div>
      </div>

      <!-- Revenda tab -->
      <div v-else-if="pedidoStore.activeTab[pedido.id] === 'tabRevenda'">
        <div class="mb-1" :title="'Razão Social: ' + pedido.revenda.revendedor.razaosocial">
          <strong>Revenda:</strong> {{ pedido.revenda.nomefantasia }}
        </div>
        <div style="margin-bottom: -5px;">
          <strong>Tempo Médio de Entrega:</strong> {{ pedido.revenda.tempo_medio_calculo > 0 ?
            "~" + Math.floor(pedido.revenda.tempo_medio_calculo) + " min" : "-" }}
        </div>

        <!-- Status switch -->
        <div class="mb-0 d-flex align-center justify-space-between">
          <strong>Status Revendedor:</strong>
          <div>
            <v-switch :model-value="pedido.revenda.revendedor.ativo === 1" @update:model-value="(val) => {
              if (val) {
                ativarRevenda(pedido);
              } else {
                // abrir modal para desativar revenda
                openDesativarRevendaModal();
              }
            }" :disabled="loadingRevendedorAction" color="primary" hide-details density="compact"
              :label="pedido.revenda.revendedor.ativo === 1 ? 'Ativo' : 'Inativo'"></v-switch>
          </div>
        </div>

        <!-- Timeline de entrega -->
        <div v-if="pedido.status !== '1'">
          <v-divider class="my-2"></v-divider>
          <div class="mb-1"><strong>Acompanhamento:</strong></div>
          <div class="mb-1" v-if="pedido.dtaceite">
            <v-icon color="success" size="small" class="mr-1">mdi-check-circle</v-icon>
            <strong>{{ verificarAceitoPeloTMKT(pedido) ? 'Aceito TMKT:' : 'Aceito em:' }}</strong> {{
              formatDate(pedido.dtaceite) }}
          </div>
          <div class="mb-1" v-if="pedido.dtsaida">
            <v-icon color="info" size="small" class="mr-1">mdi-truck-delivery</v-icon>
            <strong>Saída em:</strong> {{ formatDate(pedido.dtsaida) }}
          </div>
          <div class="mb-1" v-if="pedido.dtentrega">
            <v-icon color="success" size="small" class="mr-1">mdi-package-variant-closed-check</v-icon>
            <strong>Entregue em:</strong> {{ formatDate(pedido.dtentrega) }}
          </div>
        </div>

        <v-divider class="my-2"></v-divider>
        <div class="mb-1"><strong>Telefones:</strong></div>
        <div class="mb-1" v-if="pedido.revenda.revendedor?.telefone1">
          Fixo: {{ pedido.revenda.revendedor.telefone1 }}
          <v-icon class="copy-icon" size="x-small" color="primary"
            @click="copyToClipboard(pedido.revenda.revendedor.telefone1, 'Telefone fixo')">mdi-content-copy</v-icon>
        </div>
        <div class="mb-1" v-if="pedido.revenda.revendedor?.telefone2">
          Fixo 2: {{ pedido.revenda.revendedor.telefone2 }}
          <v-icon class="copy-icon" size="x-small" color="primary"
            @click="copyToClipboard(pedido.revenda.revendedor.telefone2, 'Telefone fixo 2')">mdi-content-copy</v-icon>
        </div>
        <div class="mb-1" v-if="pedido.revenda.revendedor?.celular">
          Celular: {{ pedido.revenda.revendedor.celular }}
          <v-icon class="copy-icon" size="x-small" color="primary"
            @click="copyToClipboard(pedido.revenda.revendedor.celular, 'Celular')">mdi-content-copy</v-icon>
          <v-icon class="action-icon whatsapp-icon" size="small" color="green" title="Notificar via WhatsApp"
            @click="openWhatsApp(pedido.revenda.revendedor.celular, pedido.revenda.nomefantasia, pedido.id, 'revenda')">mdi-whatsapp</v-icon>
        </div>
        <div class="mb-1" v-if="pedido.revenda.revendedor?.celularresponsavel">
          Responsável: {{ pedido.revenda.revendedor.celularresponsavel }}
          <v-icon class="copy-icon" size="x-small" color="primary"
            @click="copyToClipboard(pedido.revenda.revendedor.celularresponsavel, 'Celular responsável')">mdi-content-copy</v-icon>
          <v-icon class="action-icon whatsapp-icon" size="small" color="green" title="Notificar via WhatsApp"
            @click="openWhatsApp(pedido.revenda.revendedor.celularresponsavel, pedido.revenda.nomefantasia, pedido.id, 'revenda')">mdi-whatsapp</v-icon>
        </div>

        <v-divider class="my-2"></v-divider>
        <div class="mb-1"><strong>Horários de funcionamento:</strong></div>
        <div v-if="pedido.revenda.horarios_funcionamento && pedido.revenda.horarios_funcionamento.length > 0">
          <div v-for="(horario, index) in pedido.revenda.horarios_funcionamento" :key="index" class="mb-1">
            {{ horario.descricao }}: {{ horario.horainicio.substring(0, 5) }} - {{ horario.horafim.substring(0, 5) }}
          </div>
        </div>
        <div v-else class="text-body-2 text-grey">Sem horários cadastrados</div>
        <div v-if="pedido.revenda.revendedor.obsboasvindas" class="mt-2 order-observation">
          <div class="d-flex align-center justify-space-between">
            <div><strong>Obs: </strong>{{ pedido.revenda.revendedor.obsboasvindas }}</div>
            <v-icon class="edit-icon" size="small" color="grey-darken-1"
              @click="openObservationModal(pedido, 'revenda')">mdi-pencil</v-icon>
          </div>
        </div>

        <!-- Novo botão "Ligar agora" exibido quando pedido.atrasado é verdadeiro -->
        <div class="mt-1">
          <div class="d-flex align-center justify-space-between" style="gap:12px;">
            <v-tooltip location="top">
              <template #activator="{ props }">
                <span v-bind="props" style="display:inline-block">
                  <v-btn color="primary" size="small" prepend-icon="mdi-phone" @click="handleLigacaoAtrasoLocal(pedido)"
                    :loading="ligando" :disabled="desabilitarLigarAgora" style="min-width:180px">
                    Ligar agora
                  </v-btn>
                </span>
              </template>
              <span>{{ textoTooltipLigarAgora }}</span>
            </v-tooltip>

            <!-- Switch para habilitar/desabilitar recebimento de ligações pela revenda -->
            <v-switch :model-value="!!pedido.revenda?.ligacao_habilitada" @update:model-value="(val) => {
              handleToggleLigacaoRevenda(val);
            }" :loading="ligacoesLoading" :disabled="ligacoesLoading" color="primary" hide-details density="compact"
              :title="pedido.revenda.ligacao_habilitada ? 'Desabilitar ligações para revenda' : 'Habilitar ligações para revenda'"
              style="margin-right:10px" />
          </div>
        </div>
      </div>

      <!-- Ações tab -->
      <div v-else-if="pedidoStore.activeTab[pedido.id] === 'tabAcoes'">
        <v-btn color="info" block class="mb-2" size="small" prepend-icon="mdi-pencil"
          @click="openEditOrderModal(pedido)">
          Editar informações
        </v-btn>

        <v-btn color="deep-purple" block class="mb-2" size="small" prepend-icon="mdi-lifebuoy"
          @click="openAssistanceModal(pedido, 'pedido')">
          Solicitar assistência
        </v-btn>

        <v-btn v-if="isNullOrEmpty(pedido.observacao)" color="amber-darken-1" block class="mb-2" size="small"
          prepend-icon="mdi-pencil" @click="openObservationModal(pedido, 'pedido')">
          Observação do pedido
        </v-btn>

        <v-btn v-if="isNullOrEmpty(pedido.revenda?.revendedor?.obsboasvindas)" color="indigo-darken-1" block
          class="mb-2" size="small" prepend-icon="mdi-pencil" @click="openObservationModal(pedido, 'revenda')">
          Observação do revendedor
        </v-btn>

        <v-btn v-if="isNullOrEmpty(pedido.observacao_aplicativo)" color="teal" block class="mb-2" size="small"
          prepend-icon="mdi-pencil" @click="openObservationModal(pedido, 'aplicativo')">
          Observação para revenda
        </v-btn>

        <v-select density="compact" variant="outlined" :items="[
          { title: 'Aceitar', value: '2' },
          { title: 'Rejeitar', value: '4' },
          { title: 'Sair para entrega', value: '8' },
          { title: 'Entregue', value: '3' },
          { title: 'Cancelar', value: '5' }
        ]" item-title="title" item-value="value" v-model="selectedStatus" @update:model-value="onStatusChange"
          :loading="selectedStatus && isChangingStatus(selectedStatus)"
          :disabled="selectedStatus && isChangingStatus(selectedStatus)" hide-details class="mb-2">
          <template v-slot:prepend-inner>
            <v-icon size="small" color="primary">mdi-refresh</v-icon>
          </template>
          <template v-slot:label>
            <span style="color: #000000;">Alterar status</span>
          </template>
        </v-select>

        <v-select density="compact" variant="outlined" :items="[
          { title: 'Avisar Pedido', value: '1' },
          { title: 'Ligar para Cliente', value: '2' },
          { title: 'Salvar Pedido', value: '3' },
          { title: 'Pedidos Atrasados', value: '4' },
        ]" item-title="title" item-value="value" v-model="selectedSolution" @update:model-value="onSolutionChange"
          :loading="selectedSolution && isApplyingSolution(selectedSolution)"
          :disabled="selectedSolution && isApplyingSolution(selectedSolution)" hide-details class="mb-1">
          <template v-slot:prepend-inner>
            <v-icon size="small" color="primary">mdi-cog</v-icon>
          </template>
          <template v-slot:label>
            <span style="color: #000000; ">Situações Pedido</span>
          </template>
        </v-select>

        <div class="px-2" style="margin-bottom: -10px;">
          <v-switch :model-value="!!pedido.pendente" @update:model-value="handleTogglePendente(pedido)"
            :disabled="pedidoStore.loading || isPedidoOperationLoading(pedido.id, 'pendente')" color="#ff0000"
            hide-details label="Pedido Pendente" density="compact" class="suspicious-switch">
            <template v-slot:label>
              <span style="color: #000000; font-weight: bold;">Pedido Pendente</span>
            </template>
          </v-switch>
        </div>

        <!-- Ensure this switch is implemented correctly -->
        <div class="px-2" style="margin-top: 8px; margin-bottom: -10px;">
          <v-switch :model-value="pedidoStore.isPedidoEmAtencao(pedido.id)" @update:model-value="toggleAtencao"
            color="deep-purple" hide-details density="compact" class="attention-switch">
            <template #label>
              <span style="color: #000000; font-weight: bold;">Marcar para Atenção</span>
            </template>
          </v-switch>
        </div>

        <!-- Logs Pedido TMKT -->
        <v-expansion-panels class="mt-2" v-if="pedido.log_tmkt && pedido.log_tmkt.length > 0">
          <v-expansion-panel>
            <v-expansion-panel-title>
              <v-icon class="mr-2">mdi-history</v-icon>
              Histórico de Alterações
            </v-expansion-panel-title>
            <v-expansion-panel-text class="no-padding">
              <v-list class="py-0 px-1">
                <v-list-item class="px-0" v-for="(log, index) in pedido.log_tmkt" :key="index">
                  <v-list-item-title class="text-body-2">
                    {{ formatDate(log.dtnow) }} - {{ log.nomeatendente || 'Sistema' }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    Status: {{ log.status }}
                  </v-list-item-subtitle>
                </v-list-item>
              </v-list>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>
    </v-card-text>
  </v-card>

  <ConfirmationModal v-model:open="confirmationOpen" :title="confirmationConfig.title"
    :message="confirmationConfig.message" :confirm-text="confirmationConfig.confirmText"
    :cancel-text="confirmationConfig.cancelText" :confirm-color="confirmationConfig.confirmColor"
    :loading="confirmationConfig.loading" @confirm="handleConfirm" @cancel="handleCancel" />

  <RejetiarPedidoModal v-model:open="rejectionModalOpen" :pedido="pedidoToReject || pedido" :loading="rejectionLoading"
    @rejeitar="handleRejectPedido" @refazer="handleRemakePedido" @cancelar="rejectionModalOpen = false" />

  <PedidoDeliveryTimeModal v-model:open="deliveryTimeModalOpen" :pedido="pedidoToDelivery || pedido"
    :loading="deliveryTimeLoading" @confirm="handleSairParaEntrega" @cancel="deliveryTimeModalOpen = false" />

  <DesativarRevendedorModal v-model:open="deactivateModalOpen" :revendedor="selectedRevenda?.revendedor"
    @desativado="handleRevendedorDeactivated" />
</template>

<style scoped>
/* Order card styles */
.order-card {
  transition: transform 0.2s, box-shadow 0.2s;
}

.order-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1) !important;
}

/* Compact tabs */
.compact-tabs :deep(.v-tab) {
  min-width: 0 !important;
  padding: 0 8px !important;
  font-size: 0.9rem !important;
  justify-content: center;
}

.compact-tabs :deep(.v-tabs-bar) {
  height: 32px !important;
}

.compact-tab {
  text-transform: none !important;
  letter-spacing: normal !important;
}

/* Icons */
.copy-icon {
  margin-left: 4px;
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.2s, transform 0.2s;
}

.copy-icon:hover {
  opacity: 1;
  transform: scale(1.2);
}

.action-icon {
  margin-left: 6px;
  cursor: pointer;
  opacity: 0.8;
  transition: opacity 0.2s, transform 0.2s;
}

.action-icon:hover {
  opacity: 1;
  transform: scale(1.2);
}

.whatsapp-icon {
  color: #25D366 !important;
}

/* Content styling */
.order-observation {
  background-color: rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  padding: 8px;
  margin-bottom: 8px;
  transition: background-color 0.2s;
}

.order-observation:hover {
  background-color: rgba(0, 0, 0, 0.15);
}

.edit-icon {
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.2s, transform 0.2s;
}

.edit-icon:hover {
  opacity: 1;
  transform: scale(1.2);
}

/* Countdown styles */
.countdown-container {
  padding: 8px 16px 4px;
  background-color: rgba(0, 0, 0, 0.03);
}

.countdown-label {
  font-size: 0.9rem;
  font-weight: bold;
  margin-bottom: 2px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.countdown-time {
  font-family: monospace;
  font-size: 1.1rem;
  font-weight: bold;
}

.text-success {
  color: #4caf50;
}

.text-warning {
  color: #fb8c00;
}

.text-error {
  color: #f44336;
}

.expired-warning {
  background-color: rgba(244, 67, 54, 0.1);
  color: #f44336;
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: bold;
  display: flex;
  align-items: center;
}

.pedido-refeito {
  background-color: rgba(54, 67, 244, 0.1);
  color: rgb(var(--v-theme-primary));
  padding: 4px 4px;
  border-radius: 4px;
  font-weight: bold;
  display: flex;
  align-items: center;
}

/* Badge styles */
.position-relative {
  position: relative;
}

.pending-orders-badge {
  z-index: 2;
  cursor: pointer;
}

.pending-orders-badge:hover {
  transform: scale(1.1);
}

.pending-orders-badge :deep(.v-badge__badge) {
  font-size: 0.8rem;
  height: 22px;
  min-width: 22px;
  font-weight: bold;
  color: white;
  border: 1px solid white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  transition: transform 0.2s ease;
}

/* Replace the merece-atencao-container style */
.merece-atencao-container {
  position: relative;
  margin-bottom: 4px;
}

.remove-attention-btn {
  position: absolute;
  top: -4px;
  right: -4px;
  z-index: 5;
  background-color: rgba(255, 34, 34, 0.9) !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  width: 22px !important;
  height: 22px !important;
  min-width: 0 !important;
  border-radius: 50%;
}

.remove-attention-btn:hover {
  background-color: rgb(255, 34, 34) !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.4);
  transform: scale(1.2);
}

.remove-attention-btn .v-icon {
  font-size: 14px !important;
  color: white !important;
}

/* Remove padding from expansion panel text */
:deep(.no-padding) {
  padding: 0 !important;
}

:deep(.no-padding > .v-expansion-panel-text__wrapper) {
  padding: 0 !important;
}

/* Add styles for the attention switch */
.attention-switch {
  margin-top: 8px;
}

/* If the pedido is in attention, add a subtle highlight */
.order-card.in-attention {
  border-left: 4px solid #9C27B0 !important;
}
</style>
